import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  DraftedProductsResponse,
  CapacityManagementRootResponse,
  DraftedProductsParams,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: DraftedProductsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: DraftedProductsParams;
};

export function useDraftedProducts({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const draftedProducts = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      if (!rootData._links?.draftedProducts) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.draftedProducts.href)
        .expand({});

      return axios.request<DraftedProductsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    draftedProducts.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return draftedProducts;
}

export type DraftedProductsRequest = ReturnType<typeof useDraftedProducts>;
